// CEO 2026-10-07 · Page publique de signature d'un bon de remise (lien reçu par email).
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, FileDown, ClipboardCheck } from "lucide-react";
import NoteSummaryCard from "../components/equipment/NoteSummaryCard";
import { RecipientSignForm } from "../components/signature/TypedSignature";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/equipment-notes`;

export default function EquipmentNoteSign() {
    const { token } = useParams();
    const [note, setNote] = useState(null);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        axios.get(`${API}/${token}`)
            .then(({ data }) => setNote(data))
            .catch((err) => setError(err.response?.data?.detail || "Lien invalide ou expiré"));
    }, [token]);

    const sign = async (payload) => {
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/${token}/sign`, payload);
            setNote(data);
            toast.success("Merci ! Votre bon est signé.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Signature impossible");
        } finally {
            setSubmitting(false);
        }
    };

    const signed = !!note?.recipient_signed_at;

    return (
        <div className="min-h-screen bg-shariti-cream px-4 py-10" data-testid="public-equipment-sign-page">
            <div className="max-w-xl mx-auto bg-white rounded-3xl shadow-xl overflow-hidden">
                <header className="bg-shariti-black px-6 py-5 text-white">
                    <div className="text-[11px] font-black uppercase tracking-[0.2em] text-shariti-orange">Shariti · Matériel</div>
                    <h1 className="text-2xl font-black mt-1 inline-flex items-center gap-2">
                        <ClipboardCheck className="w-6 h-6" /> Bon de remise
                    </h1>
                </header>
                <div className="p-6 space-y-6">
                    {error && <p className="text-center text-sm text-red-600 py-8" data-testid="public-equipment-sign-error">{error}</p>}
                    {!error && !note && <p className="text-center text-sm text-slate-400 py-8">Chargement…</p>}
                    {note && (
                        <>
                            <p className="text-sm text-slate-600">
                                Bonjour <strong>{note.subject_full_name}</strong>, merci de vérifier le matériel ci-dessous puis de signer.
                            </p>
                            <NoteSummaryCard note={note} />
                            {signed ? (
                                <div className="text-center space-y-3 py-4" data-testid="public-equipment-sign-done">
                                    <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
                                    <p className="font-black text-slate-800">Bon signé — merci !</p>
                                    <p className="text-xs text-slate-500">Vous allez recevoir le PDF signé par email.</p>
                                    {note.status === "signed" && (
                                        <a href={`${API}/${token}/pdf`} target="_blank" rel="noreferrer"
                                            className="inline-flex items-center gap-1.5 text-sm font-bold text-shariti-orange"
                                            data-testid="public-equipment-sign-pdf">
                                            <FileDown className="w-4 h-4" /> Télécharger le PDF
                                        </a>
                                    )}
                                </div>
                            ) : (
                                <RecipientSignForm defaultName={note.subject_full_name} onSubmit={sign} submitting={submitting} testid="public-equipment-sign" />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
